import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Start every navigation at the top of the new page.
 *
 * A browser does this for free on a real page load. A single-page app gets
 * nothing: `BrowserRouter` swaps the tree and leaves the scroll offset exactly
 * where it was, so opening a listing from two thirds of the way down a card's
 * catalog page landed you two thirds of the way down the listing — the right
 * page, showing its middle, with the gallery and the price above the fold you
 * never saw. Reported on the listing rows' View, but nothing about it was
 * specific to them: NO navigation in this app reset the scroll. It reads as a
 * mobile bug because one column puts every row far below the fold, and because
 * a short destination clamps the offset back to the top on its own — which is
 * why the same click can look fine on a wide screen.
 *
 * `BrowserRouter`, not a data router, so React Router's own
 * `<ScrollRestoration>` is not available here; this is the same job by hand.
 *
 * WHY POP IS EXEMPT. Back and forward are the one case where the old offset is
 * the right answer: you are returning to a page you had already read, and the
 * browser restores your place in it via `history.scrollRestoration`. Jumping
 * to the top there would throw away a buyer's position in a grid they had
 * scrolled through — the thing this component exists to protect on the way in.
 * The first render is a POP too, which is also right: a deep link or a reload
 * arrives at the position the browser chose, not one we impose.
 *
 * ON PATHNAME, NOT THE WHOLE LOCATION. A `?page=2` or a `?q=` on the page you
 * are already on is a filter, not a new page, and those surfaces scroll
 * themselves where they want to (`Marketplace.jsx`, `SellerStore.jsx`).
 */
export function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === 'POP') return;
    // `behavior: 'auto'`, never smooth: the destination is already rendered, so
    // a smooth scroll animates THROUGH a page the reader has not asked to see.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, navigationType]);

  return null;
}

export default ScrollToTop;
